import { useCallback, useMemo } from "react";

import { flatten, isEmpty, uniq } from "lodash";
import intersection from "lodash/intersection";
import { useDispatch, useSelector } from "react-redux";

import { setSetting } from "actions";

import { Settings } from "constants/settings";

import { EMPTY_ARRAY } from "lib";

import { pluralize } from "lib/pluralize";

import {
  getPenRangeFiltersAuctionPenIds,
  getSetting,
  getUnpennedSaleLotIdsBySaleRoundId,
  selectFilteredSaleLotIds,
  selectSaleLotIdsByAuctionPenIdLookup,
} from "selectors";

import { selectMLASupplementaryDataSaleLotIdsByAuctionPenIdLookup } from "selectors/mlaSupplementaryData";

import { useGetVisibleAndTempAuctionPenIds } from "hooks/useGetVisibleAndTempAuctionPenIds";

export const useGetAuctionPenCheckBoxValues = ({
  extraFilteredPenIds,
  filterIds,
  useMlaSaleLotIds,
}) => {
  const dispatch = useDispatch();

  const selectedSaleRound = useSelector(getSetting(Settings.round));

  const unpennedSaleLotIds =
    useSelector(getUnpennedSaleLotIdsBySaleRoundId(selectedSaleRound)) ||
    EMPTY_ARRAY;

  const auctionPenSelectedSaleLotIds = useSelector(
    getSetting(Settings.auctionPenSelectedSaleLotIds),
  );

  const setAuctionPenSelectedSaleLotIds = useCallback(
    saleLotIds =>
      dispatch(setSetting(Settings.auctionPenSelectedSaleLotIds, saleLotIds)),
    [dispatch],
  );

  const globalFilteredSaleLotIds = useSelector(selectFilteredSaleLotIds);

  const saleLotByAuctionPenIdLookup = useSelector(
    selectSaleLotIdsByAuctionPenIdLookup,
  );

  const saleLotIdsBySuppDataAuctionPenId = useSelector(
    selectMLASupplementaryDataSaleLotIdsByAuctionPenIdLookup,
  );

  const filteredPenIds = useSelector(
    getPenRangeFiltersAuctionPenIds(filterIds),
  );

  const filteredPenIdSet = useMemo(() => {
    if (extraFilteredPenIds == null) {
      return new Set(filteredPenIds);
    }
    return new Set(intersection(filteredPenIds, extraFilteredPenIds.values()));
  }, [filteredPenIds, extraFilteredPenIds]);

  const [visibleAuctionPenIds, tempPensIds] = useGetVisibleAndTempAuctionPenIds(
    filteredPenIdSet,
    useMlaSaleLotIds,
  );

  const visibleAuctionPenSaleLotIds = useMemo(
    () =>
      visibleAuctionPenIds.map(penId =>
        isEmpty(saleLotByAuctionPenIdLookup)
          ? saleLotIdsBySuppDataAuctionPenId[penId]
          : Object.values(saleLotByAuctionPenIdLookup[penId]),
      ),
    [
      visibleAuctionPenIds,
      saleLotByAuctionPenIdLookup,
      saleLotIdsBySuppDataAuctionPenId,
    ],
  );

  const tempPenSaleLotIds = tempPensIds.map(penId =>
    Object.values(saleLotByAuctionPenIdLookup[penId]),
  );

  const auctionPenSaleLotIds = useMemo(
    () =>
      flatten(
        visibleAuctionPenSaleLotIds
          .concat(tempPenSaleLotIds)
          .concat(unpennedSaleLotIds),
      ).filter(
        saleLotId =>
          isEmpty(saleLotByAuctionPenIdLookup) ||
          globalFilteredSaleLotIds.includes(saleLotId),
      ),
    [
      visibleAuctionPenSaleLotIds,
      tempPenSaleLotIds,
      unpennedSaleLotIds,
      globalFilteredSaleLotIds,
      saleLotByAuctionPenIdLookup,
    ],
  );

  const allSaleLotsAreSelected = useMemo(
    () =>
      auctionPenSaleLotIds.every(saleLotId =>
        auctionPenSelectedSaleLotIds.includes(saleLotId),
      ),
    [auctionPenSaleLotIds, auctionPenSelectedSaleLotIds],
  );

  const someSaleLotsAreSelected = useMemo(
    () =>
      auctionPenSelectedSaleLotIds.some(saleLotId =>
        auctionPenSaleLotIds.includes(saleLotId),
      ) &&
      !auctionPenSaleLotIds.every(saleLotId =>
        auctionPenSelectedSaleLotIds.includes(saleLotId),
      ),
    [auctionPenSelectedSaleLotIds, auctionPenSaleLotIds],
  );
  const selectedLotsNotVisible = auctionPenSelectedSaleLotIds.filter(
    saleLotId =>
      !auctionPenSaleLotIds.includes(saleLotId) &&
      // unpenned lots are always visible
      !unpennedSaleLotIds.includes(saleLotId),
  );

  const onChange = useCallback(() => {
    if (allSaleLotsAreSelected) {
      setAuctionPenSelectedSaleLotIds(
        auctionPenSelectedSaleLotIds.filter(
          saleLotId => !auctionPenSaleLotIds.includes(saleLotId),
        ),
      );
    } else {
      setAuctionPenSelectedSaleLotIds(
        uniq(auctionPenSelectedSaleLotIds.concat(auctionPenSaleLotIds)),
      );
    }
  }, [
    allSaleLotsAreSelected,
    setAuctionPenSelectedSaleLotIds,
    auctionPenSelectedSaleLotIds,
    auctionPenSaleLotIds,
  ]);

  const info = auctionPenSelectedSaleLotIds.length
    ? `${auctionPenSelectedSaleLotIds.length} ${pluralize("Lot", auctionPenSelectedSaleLotIds.length)} Selected`
    : null;

  const warning = selectedLotsNotVisible.length
    ? `Including ${selectedLotsNotVisible.length} Not Visible`
    : null;

  return {
    checked: allSaleLotsAreSelected && !!auctionPenSaleLotIds.length,
    indeterminate: someSaleLotsAreSelected,
    disabled: !auctionPenSaleLotIds.length,
    onChange,
    warning,
    info,
  };
};
